import { configurePermissions, getFileType } from '../components/Document/DocumentUtils';
import { applySearchHighlight } from '../utils/citationJump';

/**
 * WebViewer operations service
 *
 * Provides reusable utilities for WebViewer operations:
 * - Permission configuration
 * - Text highlighting (chat-driven citation jumps)
 * - File type detection
 */

/**
 * Perform text highlighting on a document. Used by chat-driven
 * navigation: each ``highlightMetadata`` entry has a target ``page``
 * and a ``chunk_content`` to find on that page.
 *
 * Highlight pipeline (post-headless-conversion):
 * 1. Navigate to the target page so the user sees something happen
 *    even when the search misses.
 * 2. Delegate the actual search-and-highlight to
 *    ``applySearchHighlight`` from ``utils/citationJump``, which runs
 *    headlessly via ``Core.documentViewer.textSearchInit`` with the
 *    ``Search.Mode.HIGHLIGHT`` bit set so Apryse paints its own yellow
 *    band — no manual ``TextHighlightAnnotation`` plumbing, no
 *    user-visible search panel popping up.
 *
 * The previous implementation called ``instance.UI.searchText(...)``
 * which OPENS THE APRYSE SEARCH PANEL as a side effect — that's the
 * exact regression this entire PR exists to fix. If a future PR adds
 * a ``UI.searchText`` call back to this file, the
 * "never calls UI.searchText" regression test in
 * ``utils/__tests__/citationJump.test.js`` will fire — that's the
 * canary.
 */
export const performTextHighlighting = (highlightMetadata, documentViewer, instance, fileType) => {
  if (!highlightMetadata || !Array.isArray(highlightMetadata) || highlightMetadata.length === 0 || fileType !== 'pdf') {
    return;
  }

  if (!instance || !documentViewer) {
    console.warn('⚠️ Cannot highlight - instance or documentViewer not available');
    return;
  }

  // Only the first valid entry survives.
  //
  // The previous additive-annotation implementation could render
  // multiple ``TextHighlightAnnotation`` overlays at once. The
  // headless-search path uses Apryse's transient search highlights,
  // and ``applySearchHighlight`` calls ``clearSearchResults()`` before
  // each search so a stale rung doesn't leak into the next one. If we
  // forEach over every entry and fire all the searches at the same
  // 500ms tick, each search wipes the previous entry's highlight band
  // and only the last entry's highlight survives — net effect is the
  // user sees the citation land on the wrong page (and only sometimes
  // gets a highlight). Picking the first valid entry is deterministic
  // (the citation the chat layer actually selected), respects the
  // "navigate to one place" mental model of the document viewer, and
  // matches every real-world caller in tree (chat citations carry one
  // chunk; multi-entry arrays are an artifact of the legacy additive
  // shape, not a feature anyone uses today). If a future feature
  // genuinely needs multi-target highlighting, it should ship its own
  // sequential / persistent-overlay implementation rather than
  // re-introducing this race.
  const firstValid = highlightMetadata.find(({ page, chunk_content }) => {
    return Boolean(page && chunk_content && chunk_content.trim());
  });
  if (!firstValid) return;

  const { page, chunk_content } = firstValid;
  const searchText = chunk_content.trim();

  const totalPages = typeof documentViewer.getPageCount === 'function'
    ? documentViewer.getPageCount()
    : 0;
  if (totalPages && page > totalPages) {
    // Page is beyond the doc — bail rather than spinning. This is
    // a noisier failure mode than the previous setTimeout retry
    // loop, but the retry loop never terminated when the metadata
    // simply pointed at a wrong page, which is the more common
    // failure than "the page is loading right now".
    console.warn('⚠️ highlight metadata target page exceeds page count', {
      page,
      totalPages,
    });
    return;
  }

  try {
    if (typeof documentViewer.setCurrentPage === 'function') {
      documentViewer.setCurrentPage(page);
    }
    if (typeof documentViewer.updateView === 'function') {
      documentViewer.updateView();
    }
  } catch (e) {
    // Page-jump failure shouldn't abort the highlight attempt —
    // ``applySearchHighlight``'s ``displaySearchResult`` will
    // also try to scroll on the first match.
  }

  // Defer the search slightly so Apryse has a tick to render the
  // newly-active page. Without the delay, ``textSearchInit``
  // occasionally fires its callbacks before the text layer is
  // ready and reports zero matches on a page that does contain
  // the snippet.
  setTimeout(() => {
    try {
      applySearchHighlight(instance, searchText);
    } catch (error) {
      console.error('❌ Error highlighting text:', error);
    }
  }, 500);
};

// Re-export utilities from DocumentUtils for convenience
export { configurePermissions, getFileType };
